import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const AgencyTable = ({ agencies, onAgencyAction, currentLanguage = 'fr' }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [selectedAgencies, setSelectedAgencies] = useState([]);

  const labels = {
    fr: {
      selectAll: 'Tout sélectionner',
      agency: 'Agence',
      subscription: 'Abonnement',
      pilgrims: 'Pèlerins',
      revenue: 'Revenu',
      lastActivity: 'Dernière activité',
      actions: 'Actions',
      view: 'Voir',
      suspend: 'Suspendre',
      modify: 'Modifier',
      active: 'Actif',
      suspended: 'Suspendu',
      trial: 'Essai',
      noAgencies: 'Aucune agence trouvée'
    },
    ar: {
      selectAll: 'تحديد الكل',
      agency: 'الوكالة',
      subscription: 'الاشتراك',
      pilgrims: 'الحجاج',
      revenue: 'الإيرادات',
      lastActivity: 'آخر نشاط',
      actions: 'الإجراءات',
      view: 'عرض',
      suspend: 'تعليق',
      modify: 'تعديل',
      active: 'نشط',
      suspended: 'معلق',
      trial: 'تجريبي',
      noAgencies: 'لم يتم العثور على وكالات'
    }
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const handleSelectAll = () => {
    if (selectedAgencies?.length === agencies?.length) {
      setSelectedAgencies([]);
    } else {
      setSelectedAgencies(agencies?.map(a => a?.id));
    }
  };

  const handleSelectAgency = (id) => {
    setSelectedAgencies(prev =>
      prev?.includes(id) ? prev?.filter(i => i !== id) : [...prev, id]
    );
  };

  const sortedAgencies = [...agencies]?.sort((a, b) => {
    const aValue = a?.[sortConfig?.key];
    const bValue = b?.[sortConfig?.key];
    
    if (sortConfig?.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-success/10 text-success', label: labels?.[currentLanguage]?.active },
      suspended: { color: 'bg-error/10 text-error', label: labels?.[currentLanguage]?.suspended },
      trial: { color: 'bg-warning/10 text-warning', label: labels?.[currentLanguage]?.trial }
    };

    const config = statusConfig?.[status] || statusConfig?.active;
    return (
      <span className={`px-2 py-1 rounded-md caption font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // Always use en-US locale for universal date format (Western numerals)
    return date?.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (agencies?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 md:p-12 text-center">
        <Icon name="Building2" size={48} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">{labels?.[currentLanguage]?.noAgencies}</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="p-3 xl:p-4 text-left">
                <input
                  type="checkbox"
                  checked={selectedAgencies?.length === agencies?.length}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-border"
                  aria-label={labels?.[currentLanguage]?.selectAll}
                />
              </th>
              <th className="p-3 xl:p-4 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 font-medium text-foreground hover:text-primary transition-smooth text-sm xl:text-base"
                >
                  {labels?.[currentLanguage]?.agency}
                  <Icon name="ArrowUpDown" size={16} />
                </button>
              </th>
              <th className="p-3 xl:p-4 text-left">
                <button
                  onClick={() => handleSort('subscription')}
                  className="flex items-center gap-2 font-medium text-foreground hover:text-primary transition-smooth text-sm xl:text-base"
                >
                  {labels?.[currentLanguage]?.subscription}
                  <Icon name="ArrowUpDown" size={16} />
                </button>
              </th>
              <th className="p-3 xl:p-4 text-left">
                <button
                  onClick={() => handleSort('pilgrimCount')}
                  className="flex items-center gap-2 font-medium text-foreground hover:text-primary transition-smooth text-sm xl:text-base"
                >
                  {labels?.[currentLanguage]?.pilgrims}
                  <Icon name="ArrowUpDown" size={16} />
                </button>
              </th>
              <th className="p-3 xl:p-4 text-left">
                <button
                  onClick={() => handleSort('revenue')}
                  className="flex items-center gap-2 font-medium text-foreground hover:text-primary transition-smooth text-sm xl:text-base"
                >
                  {labels?.[currentLanguage]?.revenue}
                  <Icon name="ArrowUpDown" size={16} />
                </button>
              </th>
              <th className="p-3 xl:p-4 text-left">
                <button
                  onClick={() => handleSort('lastActivity')}
                  className="flex items-center gap-2 font-medium text-foreground hover:text-primary transition-smooth text-sm xl:text-base"
                >
                  {labels?.[currentLanguage]?.lastActivity}
                  <Icon name="ArrowUpDown" size={16} />
                </button>
              </th>
              <th className="p-3 xl:p-4 text-right font-medium text-foreground text-sm xl:text-base">
                {labels?.[currentLanguage]?.actions}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedAgencies?.map((agency) => (
              <tr key={agency?.id} className="hover:bg-muted/30 transition-smooth">
                <td className="p-3 xl:p-4">
                  <input
                    type="checkbox"
                    checked={selectedAgencies?.includes(agency?.id)}
                    onChange={() => handleSelectAgency(agency?.id)}
                    className="w-4 h-4 rounded border-border"
                    aria-label={`Select ${agency?.name}`}
                  />
                </td>
                <td className="p-3 xl:p-4">
                  <div className="flex items-center gap-2 xl:gap-3">
                    <div className="w-8 h-8 xl:w-10 xl:h-10 rounded-lg overflow-hidden flex-shrink-0">
                      <Image
                        src={agency?.logo}
                        alt={agency?.logoAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground text-sm xl:text-base truncate">{agency?.name}</p>
                      <p className="caption text-muted-foreground text-xs xl:text-sm truncate">{agency?.location}</p>
                    </div>
                  </div>
                </td>
                <td className="p-3 xl:p-4">
                  <div className="flex flex-col gap-1">
                    {getStatusBadge(agency?.status)}
                    <span className="caption text-muted-foreground text-xs xl:text-sm">{agency?.subscription}</span>
                  </div>
                </td>
                <td className="p-3 xl:p-4">
                  <span className="data-text font-medium text-foreground text-sm xl:text-base">{agency?.pilgrimCount}</span>
                </td>
                <td className="p-3 xl:p-4">
                  <span className="data-text font-medium text-foreground text-sm xl:text-base">{agency?.revenue}</span>
                </td>
                <td className="p-3 xl:p-4">
                  <span className="caption text-muted-foreground text-xs xl:text-sm">{formatDate(agency?.lastActivity)}</span>
                </td>
                <td className="p-3 xl:p-4">
                  <div className="flex items-center justify-end gap-1 xl:gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => onAgencyAction('view', agency?.id)}
                      className="text-xs xl:text-sm"
                    >
                      <span className="hidden xl:inline">{labels?.[currentLanguage]?.view}</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Settings"
                      onClick={() => onAgencyAction('modify', agency?.id)}
                      className="text-xs xl:text-sm"
                    >
                      <span className="hidden xl:inline">{labels?.[currentLanguage]?.modify}</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Ban"
                      onClick={() => onAgencyAction('suspend', agency?.id)}
                      className="text-xs xl:text-sm"
                    >
                      <span className="hidden xl:inline">{labels?.[currentLanguage]?.suspend}</span>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-border">
        {sortedAgencies?.map((agency) => (
          <div key={agency?.id} className="p-3 sm:p-4">
            <div className="flex items-start gap-3 mb-3">
              <input
                type="checkbox"
                checked={selectedAgencies?.includes(agency?.id)}
                onChange={() => handleSelectAgency(agency?.id)}
                className="w-4 h-4 rounded border-border mt-1"
                aria-label={`Select ${agency?.name}`}
              />
              <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                <Image
                  src={agency?.logo}
                  alt={agency?.logoAlt}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground mb-1">{agency?.name}</h4>
                <p className="caption text-muted-foreground mb-2">{agency?.location}</p>
                {getStatusBadge(agency?.status)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <p className="caption text-muted-foreground mb-1">{labels?.[currentLanguage]?.subscription}</p>
                <p className="text-sm font-medium text-foreground">{agency?.subscription}</p>
              </div>
              <div>
                <p className="caption text-muted-foreground mb-1">{labels?.[currentLanguage]?.pilgrims}</p>
                <p className="text-sm font-medium text-foreground data-text">{agency?.pilgrimCount}</p>
              </div>
              <div>
                <p className="caption text-muted-foreground mb-1">{labels?.[currentLanguage]?.revenue}</p>
                <p className="text-sm font-medium text-foreground data-text">{agency?.revenue}</p>
              </div>
              <div>
                <p className="caption text-muted-foreground mb-1">{labels?.[currentLanguage]?.lastActivity}</p>
                <p className="text-sm font-medium text-foreground">{formatDate(agency?.lastActivity)}</p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Eye"
                fullWidth
                onClick={() => onAgencyAction('view', agency?.id)}
              >
                {labels?.[currentLanguage]?.view}
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Settings"
                fullWidth
                onClick={() => onAgencyAction('modify', agency?.id)}
              >
                {labels?.[currentLanguage]?.modify}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AgencyTable;